import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { debounce, uniqueId } from 'lodash-es';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import type { RangeKeyDict } from 'react-date-range';
import getLocale from '../utils/date-fns';
import DateTimeRange from '../utils/DateTimeRange';
import type { DataSet } from './dataset';

type SelectionRangeKey = 'selection';

export type SelectionRange = {
    startDate: Date;
    endDate: Date;
    key: SelectionRangeKey;
};

type SelectionRangeState = [SelectionRange];

function convertUtcDateToTimezoneDate(date: number | Date, dataSet: DataSet): Date {
    return utcToZonedTime(date, dataSet.timezone);
}

export function convertToUtc(browserDate: Date, dataSet: DataSet): Date {
    return zonedTimeToUtc(browserDate, dataSet.timezone);
}

function useOnDataSetDateChanged(
    dataSet: DataSet,
    callback: (emitter?: string | null) => void
): void {
    useEffect(() => {
        dataSet.on('visible-updated', callback);
        dataSet.on('timezone-updated', callback);

        return () => {
            dataSet.removeListener('visible-updated', callback);
            dataSet.removeListener('timezone-updated', callback);
        };
    }, [dataSet, callback]);
}

/**
 * @param dataSet - BaseDataSet implementation that exposes min/max range and timezone details.
 * @param UUID Optional emitter ID that can be used to ignore events triggered by itself.
 */
export function useDataSetRanges(
    dataSet: DataSet,
    UUID: string | null = null
): {
    ranges: SelectionRangeState;
    setRanges: Dispatch<SetStateAction<SelectionRangeState>>;
} {
    const createRangesFromDataSet = useCallback((): SelectionRangeState => {
        const startDate = convertUtcDateToTimezoneDate(dataSet.visibleMin, dataSet);
        const endDate = convertUtcDateToTimezoneDate(dataSet.visibleMax, dataSet);
        return [
            {
                startDate,
                endDate,
                key: 'selection',
            },
        ];
    }, [dataSet]);

    const [ranges, setRanges] = useState<SelectionRangeState>(createRangesFromDataSet);

    const updateRangesFromDataSet = useMemo(
        () =>
            debounce((emitter?: string | null) => {
                if (emitter === UUID) {
                    return;
                }

                setRanges(createRangesFromDataSet);
            }, 300),
        [UUID, createRangesFromDataSet]
    );

    useOnDataSetDateChanged(dataSet, updateRangesFromDataSet);

    return { ranges, setRanges };
}

interface DataSetDateRangePickerProps {
    dataSet: DataSet;
    language: Language;
}

function DataSetDateRangePicker({ dataSet, language }: DataSetDateRangePickerProps): JSX.Element {
    const UUID = useMemo(() => uniqueId('datepicker'), []);
    const { ranges, setRanges } = useDataSetRanges(dataSet, UUID);

    const onRangeChange = useCallback(
        (startDate: Date, endDate: Date) => {
            dataSet.dateRange.setMinMax(
                convertToUtc(startDate, dataSet).getTime(),
                convertToUtc(endDate, dataSet).getTime(),
                UUID
            );
        },
        [UUID, dataSet]
    );

    return (
        <div className="dataset-date-range-picker-container">
            <DateTimeRange
                onChange={({ selection }: RangeKeyDict) => {
                    if (!selection?.startDate || !selection?.endDate) {
                        return;
                    }

                    const nextRange: SelectionRange = {
                        key: selection.key as SelectionRangeKey,
                        startDate: selection.startDate,
                        endDate: selection.endDate,
                    };
                    setRanges([nextRange]);
                    onRangeChange(selection.startDate, selection.endDate);
                }}
                moveRangeOnFirstSelection={false}
                months={2}
                ranges={ranges}
                rangeColors={['#F8DA28']}
                direction="horizontal"
                preventSnapRefocus={true}
                calendarFocus="backwards"
                locale={getLocale(language)}
                className="small"
            />
        </div>
    );
}

export default DataSetDateRangePicker;
